import autoTable from 'jspdf-autotable';
import {
  addImage,
  addText,
  createPdfDocument,
  isNewPageNeeded,
  setFont,
  subtitleSize,
  titleSize,
  x,
  y,
} from '../pdf';
import LogoCiva from '../../resources/images/logo-civa.png';
import { PEN_SYMBOL } from '../../config/constants';

const myX = x;
let myY = y;
let pageWidth = 0;
let halfPage = 0;
let thirdPartPage = 0;

const printBaggages = (doc, baggages) => {
  const columns = [
    { title: 'N°', dataKey: 'item' },
    { title: 'N° Boleto', dataKey: 'ticket' },
    { title: 'N° Ticket Equipaje', dataKey: 'itemTicket' },
    { title: 'Embarque', dataKey: 'origin' },
    { title: 'Desembarque', dataKey: 'destination' },
    { title: 'Categoría', dataKey: 'category' },
    { title: 'E. Peso', dataKey: 'weight' },
    { title: 'E. Precio', dataKey: 'excessPrice' },
  ];

  let rowNumber = 0;
  let totalWeight = 0;
  let totalExcessPrice = 0;

  const data = [];

  baggages.forEach((baggage) => {
    const tickets = baggage.ticketList
      .map((ticket) => ticket.ticketCode)
      .join('\n');

    const sourceLocations = [
      ...new Set(
        baggage.ticketList.map((ticket) => ticket.sourceLocation.name),
      ),
    ].join('\n');

    const destinationLocations = [
      ...new Set(
        baggage.ticketList.map((ticket) => ticket.destinationLocation.name),
      ),
    ].join('\n');

    if (baggage.excessBaggage) {
      totalExcessPrice += Number(baggage.excessBaggage.price);
    }

    baggage.itemList.forEach((item, index) => {
      rowNumber += 1;

      const isFirstItemOfBaggage = index === 0;

      totalWeight += Number(item.weight) || 0;

      const row = {
        item: rowNumber,
        ticket: tickets,
        itemTicket: item.itemCorrelation,
        origin: sourceLocations,
        destination: destinationLocations,
        category: item.itemCategoryName || item.description,
        weight: `${item.weight} kg`,
        excessPrice:
          isFirstItemOfBaggage && baggage.excessBaggage
            ? `${PEN_SYMBOL} ${baggage.excessBaggage.price}`
            : null,
      };

      data.push(row);
    });
  });

  const footRow = {
    category: 'Total',
    weight: `${Number(totalWeight.toFixed(2))} kg`,
    excessPrice: `${PEN_SYMBOL} ${totalExcessPrice.toFixed(2)}`,
  };

  autoTable(doc, {
    columns,
    body: data,
    foot: [footRow],
    showFoot: 'lastPage',
    startY: (myY += 5),
    showHeader: 'everyPage',
    margin: 5,
    theme: 'striped',
    headStyles: { fillColor: [255, 255, 255] },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: 0,
      fontStyle: 'bold',
    },
    styles: {
      overflow: 'hidden',
      cellPadding: 0.5,
      textColor: 0,
    },
  });
};

const AGENCY_SUMMARY_COLUMNS = [
  { title: 'Agencia', dataKey: 'agency' },
  { title: 'Cantidad Ticket Equipaje', dataKey: 'baggageCount' },
  { title: 'N° Ítems', dataKey: 'itemCount' },
  { title: 'Peso Total', dataKey: 'weight' },
  { title: 'Exceso Total', dataKey: 'excessPrice' },
];

const summarizeByLocation = (baggages, locationKey) => {
  const summary = {};

  baggages.forEach((baggage) => {
    const itemCount = baggage.itemList.length;
    const weight = baggage.itemList.reduce(
      (total, item) => total + (Number(item.weight) || 0),
      0,
    );
    const excessPrice = baggage.excessBaggage
      ? Number(baggage.excessBaggage.price)
      : 0;

    const agencyLabels = new Set(
      baggage.ticketList.map(({ [locationKey]: location }) =>
        location.agencyCode
          ? `${location.name} (${location.agencyCode})`
          : location.name,
      ),
    );

    agencyLabels.forEach((agencyLabel) => {
      if (!summary[agencyLabel]) {
        summary[agencyLabel] = {
          baggageCount: 0,
          itemCount: 0,
          weight: 0,
          excessPrice: 0,
        };
      }

      summary[agencyLabel].baggageCount += 1;
      summary[agencyLabel].itemCount += itemCount;
      summary[agencyLabel].weight += weight;
      summary[agencyLabel].excessPrice += excessPrice;
    });
  });

  return Object.entries(summary).map(([agency, totals]) => ({
    agency,
    baggageCount: totals.baggageCount,
    itemCount: totals.itemCount,
    weight: `${Number(totals.weight.toFixed(2))} kg`,
    excessPrice: `${PEN_SYMBOL} ${totals.excessPrice.toFixed(2)}`,
  }));
};

const printAgencySummaryTable = (doc, title, rows) => {
  myY = isNewPageNeeded(doc, myX, myY, 20 + rows.length * 6);

  addText(doc, title, myX, (myY += 8), null, 'left');

  autoTable(doc, {
    columns: AGENCY_SUMMARY_COLUMNS,
    body: rows,
    startY: (myY += 3),
    margin: 5,
    theme: 'striped',
    headStyles: { fillColor: [255, 255, 255] },
    styles: {
      overflow: 'hidden',
      cellPadding: 0.5,
      textColor: 0,
    },
  });

  myY = doc.lastAutoTable.finalY;
};

const printAgencySummary = (doc, baggages) => {
  const receivedByAgency = summarizeByLocation(baggages, 'sourceLocation');
  const toDeliverByAgency = summarizeByLocation(
    baggages,
    'destinationLocation',
  );

  myY = isNewPageNeeded(doc, myX, myY, 20);
  doc.setFontSize(subtitleSize);
  setFont(doc, 'bold');
  addText(doc, 'RESUMEN POR AGENCIA', myX, (myY += 10), null, 'left');
  setFont(doc);

  printAgencySummaryTable(
    doc,
    'Total Recibido por Agencia de Origen',
    receivedByAgency,
  );
  printAgencySummaryTable(
    doc,
    'Total por Entregar por Agencia de Destino',
    toDeliverByAgency,
  );
};

const printBaggageManifest = (manifest) => {
  const doc = createPdfDocument();

  pageWidth = doc.internal.pageSize.getWidth();
  halfPage = pageWidth / 2;
  thirdPartPage = pageWidth / 3;
  myY = 10;

  let previousLineHeight = 0;

  addImage(doc, LogoCiva, myX, 3, 35, 10);

  // Title
  doc.setFontSize(titleSize);
  setFont(doc, 'bold');
  addText(doc, 'MANIFIESTO DE EQUIPAJES', halfPage, myY, halfPage);
  setFont(doc);

  doc.setFontSize(subtitleSize);

  // 1° line
  addText(doc, `Fecha: ${manifest.tripDate}`, myX, (myY += 10));

  const origin = doc.splitTextToSize(
    `Origen: ${manifest.origin}`,
    thirdPartPage - myX / 2,
  );
  addText(doc, origin, thirdPartPage, myY);

  const destination = doc.splitTextToSize(
    `Destino: ${manifest.destination}`,
    thirdPartPage - myX / 2,
  );
  addText(doc, destination, thirdPartPage * 2, myY);

  previousLineHeight =
    destination.length > origin.length ? destination.length : origin.length;

  // 2° line
  addText(
    doc,
    `Manifiesto: ${manifest.manifestNumber}`,
    myX,
    (myY += 5 * previousLineHeight),
  );

  addText(doc, `ID Itinerario: ${manifest.itineraryId}`, thirdPartPage, myY);

  // 3° line
  addText(doc, `Hora de Salida: ${manifest.tripTime}`, myX, (myY += 10));

  addText(doc, `Servicio: ${manifest.serviceTypeName}`, halfPage / 2, myY);

  addText(
    doc,
    `# Bus: ${manifest.bus ? manifest.bus.companyBusId : ' - '}`,
    halfPage - 10,
    myY,
  );

  addText(doc, 'Conductores:', halfPage + 20, myY);
  manifest.drivers.forEach((driver, index) => {
    let driverFullName = driver.fullName;
    if (driverFullName.length > 30)
      driverFullName = `${driverFullName.substring(0, 30)}...`;
    addText(doc, driverFullName, halfPage + 45, index !== 0 ? (myY += 5) : myY);
  });

  // 4° line
  addText(doc, `Manifiesto emitido por: ${manifest.user}`, myX, (myY += 5));

  // Print baggage manifest
  printBaggages(doc, manifest.baggageList);

  // Print totals per agency (received by origin, to deliver by destination)
  myY = doc.lastAutoTable.finalY;
  printAgencySummary(doc, manifest.baggageList);

  window.open(doc.output('bloburl'), '_blank');
};

// eslint-disable-next-line import/prefer-default-export
export { printBaggageManifest };
